import { Europafacts } from "@/services/api";
import { logger } from "@/utils/debug";
import type { AuthStore } from "@/types/stores/auth";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const initialState = {
  user: null,
  loading: true,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setUser: (user) => set({ user }, false, "auth/setUser"),

      setLoading: (loading) => set({ loading }, false, "auth/setLoading"),

      signIn: async (username, password) => {
        try {
          await Europafacts.login(username, password);
          const currentUser = await Europafacts.getCurrentUser();
          set({ user: currentUser }, false, "auth/signIn");
          return { error: null };
        } catch (error: any) {
          logger.error("Login failed:", error);
          return { error: { message: error.message || "Login failed" } };
        }
      },

      signOut: async () => {
        Europafacts.logout();
        set({ user: null }, false, "auth/signOut");
      },

      initializeAuth: async () => {
        try {
          if (Europafacts.isAuthenticated()) {
            const currentUser = await Europafacts.getCurrentUser();
            set(
              { user: currentUser, loading: false },
              false,
              "auth/initializeAuth"
            );
          } else {
            set({ loading: false }, false, "auth/initializeAuth");
          }
        } catch (error) {
          logger.error("Failed to initialize auth:", error);
          Europafacts.logout();
          set({ user: null, loading: false }, false, "auth/initializeAuth");
        }
      },

      handleTokenExpiration: () => {
        set({ user: null }, false, "auth/handleTokenExpiration");
      },
    }),
    {
      name: "auth-store",
    }
  )
);

export const useUser = () => useAuthStore((state) => state.user);
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useSignIn = () => useAuthStore((state) => state.signIn);
export const useSignOut = () => useAuthStore((state) => state.signOut);
export const useInitializeAuth = () =>
  useAuthStore((state) => state.initializeAuth);
export const useAuth = () => useAuthStore();

